import * as React from 'react';
import { Noun, Verb, trackUe } from '@trello/analytics';
import { forNamespace } from 'app/common/lib/util/i18n';
import { SHARE_TEMPLATE_POPOVER } from 'app/gamma/src/modules';
import Icon, { IconColor, IconName } from 'app/common/components/controls/icon';
import { Popover, PopoverProps } from 'app/gamma/src/components/popover';
import styles from './ShareTemplatePopover.less';
import { LinkCopy } from 'app/src/components/LinkCopy';

const format = forNamespace('share template popover', {
  shouldEscapeStrings: false,
});

interface ShareTemplatePopoverProps {
  shouldRenderWithPopoverComponent?: boolean;
  target?: PopoverProps['target'];
  orgName: string | undefined;
  enterpriseName: string | undefined;
  shortUrl: string;
  visibility: string;
  boardId?: string;
  teamId?: string;
}

interface VisibilityTextProps {
  visibility: string;
  orgName: string | undefined;
  enterpriseName: string | undefined;
}

const VisibilityText = (props: VisibilityTextProps) => {
  // Show visibility message for team and public boards
  const { visibility, orgName, enterpriseName } = props;
  let text;
  let icon;

  if (visibility === 'public') {
    text = format('anyone on the internet can see this template');
    icon = (
      <Icon
        className={styles.visibilityIcon}
        name={IconName.Public}
        color={IconColor.Green}
      />
    );
  } else if (enterpriseName && visibility === 'enterprise') {
    text = format('all members of org can see this template', {
      orgName: enterpriseName,
    });
    icon = (
      <Icon
        className={styles.visibilityIcon}
        name={IconName.OrganizationVisible}
        color={IconColor.Yellow}
      />
    );
  } else if (orgName && visibility === 'org') {
    text = format('all members of org can see this template', {
      orgName,
    });
    icon = (
      <Icon
        className={styles.visibilityIcon}
        name={IconName.OrganizationVisible}
        color={IconColor.Yellow}
      />
    );
  }

  if (text && icon) {
    return (
      <div className="visibility-text">
        {icon}
        <span className={styles.visibilityText}>{text}</span>
      </div>
    );
  }
};

export const ShareTemplatePopover = (props: ShareTemplatePopoverProps) => {
  const {
    shouldRenderWithPopoverComponent,
    target,
    orgName,
    enterpriseName,
    shortUrl,
    visibility,
    boardId,
    teamId,
  } = props;

  const text = {
    description: format('share a view only link with your team'),
    learnMore: format('learn more about templates'),
  };

  const onClick = (eventContext: object) => {
    return trackUe({
      category: Noun.SHARE_TEMPLATE_MENU,
      verb: Verb.OPENS,
      directObj: Noun.HELP_DOC_LINK,
      context: {
        boardId: boardId || '',
        teamId: teamId || '',
      },
    });
  };

  const inner = (
    <div className={styles.popoverContent}>
      <p>{text.description}</p>
      <LinkCopy
        shortUrl={shortUrl}
        eventCategory={Noun.SHARE_TEMPLATE_MENU}
        boardId={boardId}
        teamId={teamId}
      />
      {VisibilityText({ visibility, orgName, enterpriseName })}
      <hr />
      <a
        href="https://help.trello.com/article/1187-creating-template-boards"
        className={styles.learnMoreLink}
        onClick={onClick}
        rel="noopener noreferrer"
        target="_blank"
        title={text.learnMore}
      >
        {text.learnMore}
      </a>
    </div>
  );

  if (shouldRenderWithPopoverComponent) {
    return (
      <Popover name={SHARE_TEMPLATE_POPOVER} target={target}>
        {() => <div>{inner}</div>}
      </Popover>
    );
  } else {
    return inner;
  }
};
